import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { TooltipArrow } from "@radix-ui/react-tooltip";

type Option = {
  label: string;
  value: string;
};

// New component for reusable multi-select
interface MultiSelectProps {
  values: string[];
  options: Option[];
  onChange: (value: string) => void;
  title?: string;
  placeholder?: string;
  className?: string;
  maxVisibleOptions?: number;
  onSearchChange?: (searchTerm: string) => void;
}

const MultiSelect = ({
  values = [],
  options,
  onChange,
  title = "Select options",
  placeholder = "Search options...",
  className,
  maxVisibleOptions = 2,
  onSearchChange,
}: MultiSelectProps) => {
  const selectedValues = new Set(values);

  const renderTrigger = () => {
    if (selectedValues.size > 0) {
      const selectedOptions = options.filter((option) =>
        selectedValues.has(option.value)
      );
      return (
        <>
          <div className="hidden space-x-1 lg:flex">
            {selectedOptions.slice(0, maxVisibleOptions).map((option) => (
              <Badge
                key={option.value}
                variant="secondary"
                className="rounded-sm px-1 font-normal truncate"
              >
                <span className="max-w-[80px] truncate">{option.label}</span>
              </Badge>
            ))}
            {selectedValues.size > maxVisibleOptions && (
              <Tooltip>
                <TooltipTrigger>
                  <Badge
                    variant="secondary"
                    className="rounded-sm px-1 font-normal"
                  >
                    +{selectedValues.size - maxVisibleOptions} more
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="bg-black text-white">
                  <TooltipArrow />
                  <p>
                    {selectedOptions?.slice(maxVisibleOptions).map((option) => (
                      <span key={option.value}>
                        {option.label}
                        <br />
                      </span>
                    ))}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <Badge
            variant="secondary"
            className="rounded-sm px-1 font-normal lg:hidden"
          >
            {selectedValues.size}
          </Badge>
        </>
      );
    }
    return <span>{title}</span>;
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className={cn("justify-between", className)}>
          {renderTrigger()}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
      >
        <Command>
          <CommandInput 
            placeholder={placeholder} 
            onValueChange={onSearchChange}
          />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options
                .sort((a, b) => {
                  const aSelected = selectedValues.has(a.value);
                  const bSelected = selectedValues.has(b.value);
                  if (aSelected && !bSelected) return -1;
                  if (!aSelected && bSelected) return 1;
                  return 0;
                })
                .map((option) => {
                  const isSelected = selectedValues.has(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      onSelect={() => onChange(option.value)}
                    >
                      <div
                        className={cn(
                          "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                          isSelected
                            ? "bg-primary text-primary-foreground"
                            : "opacity-50 [&_svg]:invisible"
                        )}
                      >
                        <Check className="h-3 w-3" />
                      </div>
                      <span>{option.label}</span>
                    </CommandItem>
                  );
                })}
            </CommandGroup>
            {selectedValues.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => {
                      values.forEach((value) => onChange(value)); // Clear all selected values
                    }}
                    className="justify-center text-center"
                  >
                    Clear selection
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default MultiSelect;
